import { useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { toast } from 'sonner';
import { ReactComponent as ArrowRightIcon } from '@rocco/icons/svg/arrow/arrow-right.svg';
import type { PinItemType } from '~/modules/moodboards/type';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useAuth } from '@modules/root/context';
import { isUserLoggedIn, useUser } from '@modules/user';
import { decodeHTMLString } from '~/modules/seo/util/decode-markdown';
import type { FollowSelectorItem } from '~/modules/moodboards/types';
import { ACTION_ADD_ITEMS_TO_MOOD_BOARD } from '~/routes/actions.add-items-to-mood-board';
import { REMOVE_INSPIRATION_ITEMS_BY_PAGE_API_ROUTE } from '~/routes/actions.remove-items-from-mood-board-by-page';
import {
    addPinMembership,
    addSavedPinKeys,
    cloneMembershipCache,
    lastUsedBoardIdAtom,
    makePinKey,
    pinMembershipCacheAtom,
    removePinMembership,
    removeSavedPinKeys,
    resolveTrackedPinId,
    savedPinKeysAtom,
} from '~/modules/moodboards/state/design-board-save-state';

interface ItemToFollow {
    id: number;
    libraryItemId?: number;
}

/**
 * Custom hook to handle following/unfollowing items on mood boards.
 *
 * @returns An object containing board save/remove handlers and isSavedToBoard.
 */
export const useHandleFollow = () => {
    const savedPinKeys = useAtomValue(savedPinKeysAtom);
    const pinMembershipCache = useAtomValue(pinMembershipCacheAtom);
    const setSavedPinKeys = useSetAtom(savedPinKeysAtom);
    const setPinMembershipCache = useSetAtom(pinMembershipCacheAtom);
    const setLastUsedBoardId = useSetAtom(lastUsedBoardIdAtom);
    const user = useUser();
    const isLoggedIn = isUserLoggedIn(user);
    const { showAuthModal } = useAuth();
    const { mobile } = useAppDisplayModeContext();

    /**
     * Checks if an item is already saved to a mood board.
     *
     * @param itemId - The ID of the item to check. For PhotoPins, this is the LibraryImageID.
     * @param itemType - The type of the item to check.
     * @returns True if the item is saved, false otherwise.
     */
    const isInDesignBoard = useCallback(
        (itemId: number, itemType?: PinItemType) => {
            return savedPinKeys.has(makePinKey(itemId, itemType));
        },
        [savedPinKeys],
    );

    /**
     * Helper function to show success toast after saving to a board.
     */
    const showSaveSuccessToast = useCallback(
        (selectedBoard: FollowSelectorItem, isMobile: boolean) => {
            const boardTitle = decodeHTMLString(selectedBoard.title);
            // Build the target URL for viewing the board
            let targetUrl = '';
            if (selectedBoard.isPersonal && selectedBoard.urlSegment) {
                targetUrl = `/app/boards/${selectedBoard.urlSegment}`;
            } else if (
                selectedBoard.projectUrlSegment &&
                selectedBoard.urlSegment
            ) {
                targetUrl = `/app/project/${selectedBoard.projectUrlSegment}/boards/${selectedBoard.urlSegment}`;
            } else if (selectedBoard.urlSegment) {
                // Fallback when board metadata is partial.
                targetUrl = `/app/boards/${selectedBoard.urlSegment}`;
            }

            // Use window.location for navigation because Remix's navigate()
            // can be swallowed by a concurrent fetcher revalidation triggered
            // by the save action that fired just before this toast appeared.
            const handleNavigate = () => {
                toast.dismiss();
                if (targetUrl) {
                    if (isLoggedIn) {
                        window.location.href = targetUrl;
                    } else {
                        showAuthModal({
                            authSource: 'designBoard',
                            redirect: targetUrl,
                        });
                    }
                } else if (!isLoggedIn) {
                    showAuthModal({
                        authSource: 'designBoard',
                        redirect: '/app/boards',
                    });
                }
            };

            if (!isMobile) {
                toast.success(`Saved to ${boardTitle}`, {
                    cancel: targetUrl
                        ? {
                              label: (
                                  <span className="flex items-center justify-between gap-2">
                                      <span>View board</span>
                                      <ArrowRightIcon className="size-4.5" />
                                  </span>
                              ),
                              onClick: handleNavigate,
                          }
                        : undefined,
                    action: {
                        label: 'Dismiss',
                        onClick: () => toast.dismiss(),
                    },
                    classNames: {
                        cancelButton:
                            'group-[.toast]:bg-brick-50! group-[.toast]:text-primary-foreground! group-[.toast]:font-medium! group-[.toast]:hover:bg-brick-200!',
                        actionButton:
                            'group-[.toast]:bg-gray-300! group-[.toast]:text-gray-900! group-[.toast]:font-normal! group-[.toast]:hover:bg-gray-400!',
                    },
                });
            } else {
                toast.success(`Saved to ${boardTitle}`, {
                    action: targetUrl
                        ? {
                              label: 'View',
                              onClick: handleNavigate,
                          }
                        : {
                              label: 'Dismiss',
                              onClick: () => toast.dismiss(),
                          },
                });
            }
        },
        [isLoggedIn, showAuthModal],
    );

    /**
     * Handles saving items to a specific board.
     * Used by TileFollowButtonWithPopover when a board is selected.
     *
     * @param selectedBoard - The board to save to.
     * @param items - The item(s) to save.
     * @param itemType - The type of items being saved.
     * @returns `true` if save was successful.
     */
    const handleSaveToBoard = useCallback(
        async (
            selectedBoard: FollowSelectorItem,
            items: ItemToFollow | ItemToFollow[],
            itemType: PinItemType,
        ): Promise<boolean> => {
            const itemsToFollow = (
                Array.isArray(items) ? items : [items]
            ).filter((item) => item.id !== 0);

            if (!itemsToFollow.length) {
                return false;
            }

            let previousSavedPinKeys = new Set(savedPinKeys);
            let previousMembershipCache =
                cloneMembershipCache(pinMembershipCache);

            // Add items to the selected board
            try {
                const itemIds = itemsToFollow.map(({ id }) => id);
                const pinKeys = itemsToFollow.map(({ id, libraryItemId }) =>
                    makePinKey(
                        resolveTrackedPinId(id, libraryItemId, itemType),
                        itemType,
                    ),
                );
                previousSavedPinKeys = new Set(savedPinKeys);
                previousMembershipCache =
                    cloneMembershipCache(pinMembershipCache);

                // Optimistic updates for instant UI feedback.
                setSavedPinKeys((prev: Set<string>) =>
                    addSavedPinKeys(prev, pinKeys),
                );
                setPinMembershipCache((prev) =>
                    addPinMembership(prev, pinKeys, selectedBoard.id),
                );
                setLastUsedBoardId(selectedBoard.id);

                const formData = new FormData();
                formData.append('boardId', String(selectedBoard.id));
                formData.append('itemsType', itemType);
                itemIds.forEach((itemId) => {
                    formData.append('itemIds[]', String(itemId));
                });

                const response = await fetch(ACTION_ADD_ITEMS_TO_MOOD_BOARD, {
                    method: 'post',
                    credentials: 'same-origin',
                    body: formData,
                });

                if (!response.ok) {
                    throw new Error('Save to mood board failed');
                }

                // Show success toast
                showSaveSuccessToast(selectedBoard, mobile);

                return true;
            } catch (error) {
                console.error('Error adding items to board:', error);
                // Roll back optimistic updates when the request fails.
                setSavedPinKeys(previousSavedPinKeys);
                setPinMembershipCache(previousMembershipCache);
                toast.error('Failed to save to board');
                return false;
            }
        },
        [
            savedPinKeys,
            pinMembershipCache,
            showSaveSuccessToast,
            mobile,
            setSavedPinKeys,
            setPinMembershipCache,
            setLastUsedBoardId,
        ],
    );

    /**
     * Helper function to show success toast after removing from a board.
     */
    const showRemoveSuccessToast = useCallback(
        (selectedBoard: FollowSelectorItem) => {
            const boardTitle = decodeHTMLString(selectedBoard.title);
            toast.success(`Removed from ${boardTitle}`, {
                action: {
                    label: 'Dismiss',
                    onClick: () => toast.dismiss(),
                },
                classNames: {
                    actionButton:
                        'group-[.toast]:bg-gray-300! group-[.toast]:text-gray-900! group-[.toast]:font-normal! group-[.toast]:hover:bg-gray-400!',
                },
            });
        },
        [],
    );

    /**
     * Handles removing items from a specific board.
     * Used by FollowSelector when a saved item is selected (to toggle off).
     *
     * @param selectedBoard - The board to remove from.
     * @param items - The item(s) to remove.
     * @param itemType - The type of items being removed.
     * @returns `true` if removal was successful.
     */
    const handleRemoveFromBoard = useCallback(
        async (
            selectedBoard: FollowSelectorItem,
            items: ItemToFollow | ItemToFollow[],
            itemType: PinItemType,
        ): Promise<boolean> => {
            const itemsToRemove = (
                Array.isArray(items) ? items : [items]
            ).filter((item) => item.id !== 0);

            if (!itemsToRemove.length) {
                return false;
            }

            let previousSavedPinKeys = new Set(savedPinKeys);
            let previousMembershipCache =
                cloneMembershipCache(pinMembershipCache);

            try {
                // Use libraryItemId if available, otherwise use id
                const isPhotoPin = itemType === 'PhotoPin';
                const pageIds = itemsToRemove.map(({ id, libraryItemId }) =>
                    isPhotoPin ? String(id) : String(libraryItemId ?? id),
                );
                const pinKeys = itemsToRemove.map(({ id, libraryItemId }) =>
                    makePinKey(
                        resolveTrackedPinId(id, libraryItemId, itemType),
                        itemType,
                    ),
                );
                previousSavedPinKeys = new Set(savedPinKeys);
                previousMembershipCache =
                    cloneMembershipCache(pinMembershipCache);

                const optimisticResult = removePinMembership(
                    pinMembershipCache,
                    pinKeys,
                    selectedBoard.id,
                );
                setPinMembershipCache(optimisticResult.cache);
                setSavedPinKeys((prev: Set<string>) =>
                    removeSavedPinKeys(
                        prev,
                        optimisticResult.keysRemovedFromAllBoards,
                    ),
                );

                const formData = new FormData();
                formData.append('boardId', String(selectedBoard.id));
                pageIds.forEach((pageId) => {
                    formData.append('pageIds[]', pageId);
                });

                const response = await fetch(
                    REMOVE_INSPIRATION_ITEMS_BY_PAGE_API_ROUTE,
                    {
                        method: 'post',
                        credentials: 'same-origin',
                        body: formData,
                    },
                );

                if (!response.ok) {
                    throw new Error('Remove from mood board failed');
                }

                // Show success toast
                showRemoveSuccessToast(selectedBoard);

                return true;
            } catch (error) {
                console.error('Error removing items from board:', error);
                // Roll back optimistic updates when the request fails.
                setSavedPinKeys(previousSavedPinKeys);
                setPinMembershipCache(previousMembershipCache);
                toast.error('Failed to remove from board');
                return false;
            }
        },
        [
            savedPinKeys,
            pinMembershipCache,
            showRemoveSuccessToast,
            setSavedPinKeys,
            setPinMembershipCache,
        ],
    );

    return {
        handleSaveToBoard,
        handleRemoveFromBoard,
        isInDesignBoard,
    };
};
